// ─── Lens Context ────────────────────────────────────────────────────────────
//
// React context that exposes the current dashboard "lens" (patient vs
// clinician) and a setter. The lens controls which LensCopy variant the
// dashboard sections render.
//
// Persisted under localStorage key `arete_dashboard_lens`. Default is
// `patient` per the approved plan — clinicians toggle once per device and
// their preference persists.
//
// The provider is mounted in src/App.tsx inside the existing provider nest
// (ThemeProvider → BrowserRouter → DemoProvider → LabRangeProvider → LensProvider).
// Consumers use the `useLens()` hook. Consumers that just need the raw lens
// value in a pure function should accept it as a prop rather than reaching
// into context — this keeps the transformer layer pure.
// ──────────────────────────────────────────────────────────────────────────────

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Lens } from './types';

const STORAGE_KEY = 'arete_dashboard_lens';
const DEFAULT_LENS: Lens = 'patient';

function readStoredLens(): Lens {
  if (typeof window === 'undefined') return DEFAULT_LENS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'patient' || raw === 'clinician') return raw;
  } catch {
    /* localStorage unavailable (private mode, quota exceeded) */
  }
  return DEFAULT_LENS;
}

function writeStoredLens(lens: Lens): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, lens);
  } catch {
    /* ignore */
  }
}

interface LensContextValue {
  lens: Lens;
  setLens: (next: Lens) => void;
  toggle: () => void;
}

const LensContext = createContext<LensContextValue | null>(null);

interface LensProviderProps {
  children: React.ReactNode;
  /** Override for tests — bypasses localStorage. */
  initialLens?: Lens;
  /**
   * Smart first-time default. Applied once, only when localStorage had no
   * stored value at mount — so it never overrides a user's explicit
   * preference. Used by App.tsx to auto-default clinicians to the clinician
   * lens on their first-ever load.
   */
  defaultLensHint?: Lens;
}

/**
 * LensProvider — source of truth for the current dashboard lens.
 *
 * Lazy-initializes from localStorage on first render so the UI matches the
 * user's persisted preference without a flash. Writes back to localStorage
 * whenever the lens changes.
 *
 * If `defaultLensHint` is supplied AND localStorage had no value at mount,
 * the hint is applied exactly once (and persisted). This lets a clinician's
 * first-ever load skip the patient default without overriding any later
 * explicit toggle.
 */
export function LensProvider({ children, initialLens, defaultLensHint }: LensProviderProps) {
  const [storageWasEmpty] = useState(() => {
    if (initialLens) return false;
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(STORAGE_KEY) === null;
    } catch {
      return false;
    }
  });

  const [lens, setLensState] = useState<Lens>(() => initialLens ?? readStoredLens());
  const [hintApplied, setHintApplied] = useState(false);

  // Apply defaultLensHint once on first-ever device load. Subsequent loads
  // skip this branch because storageWasEmpty is captured at mount and the
  // user's stored preference takes precedence.
  useEffect(() => {
    if (hintApplied) return;
    if (!storageWasEmpty) return;
    if (!defaultLensHint) return;
    setLensState(defaultLensHint);
    setHintApplied(true);
  }, [hintApplied, storageWasEmpty, defaultLensHint]);

  useEffect(() => {
    writeStoredLens(lens);
  }, [lens]);

  const setLens = useCallback((next: Lens) => {
    setLensState(next);
  }, []);

  const toggle = useCallback(() => {
    setLensState((prev) => (prev === 'patient' ? 'clinician' : 'patient'));
  }, []);

  const value = useMemo<LensContextValue>(
    () => ({ lens, setLens, toggle }),
    [lens, setLens, toggle],
  );

  return <LensContext.Provider value={value}>{children}</LensContext.Provider>;
}

/**
 * useLens — read the current lens and its setters. Throws if used outside
 * a LensProvider so misuse is caught in development.
 */
export function useLens(): LensContextValue {
  const ctx = useContext(LensContext);
  if (!ctx) {
    throw new Error('useLens must be used within a LensProvider');
  }
  return ctx;
}

/** Exported only for test resets. */
export const __LENS_STORAGE_KEY__ = STORAGE_KEY;
